import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus,
  faStar,
  faDoorOpen,
  faCrosshairs,
  faTrophy,
  faHandPaper,
  faHandBackFist,
  faCircleUp,
  faCircleDown,
  faRotate,
  faArrowsLeftRight,
  faBomb,
  faReply,
  faShield,
  faTableTennis,
  faBullseye,
  faRuler,
  faCircle,
  faArrowRight,
  faArrowUp,
  faGaugeHigh,
  faPersonWalking,
  faHeartbeat,
  faUserNinja,
  faArrowsUpDown,
  faFire,
  faHands,
  faDumbbell,
  faRoad,
  faStairs,
  faClock,
  faBackward,
  faPersonRunning,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";

import { AddHeader } from "../../components/AddHeader";
import { ExerciseTypeMark } from "../../components/ExerciseTypeMark";
import { ExerciseAddLayout } from "./ExerciseAddLayout";
import { getsubtype } from "../../components/funtionsBD/Mainservice";
import { competencyBlockIconSrc } from "../../utils/competencyBlockIcon";
import { exerciseTypeIconSrc } from "../../utils/exerciseTypeIcon";
import {
  isCompetencyBlockExerciseType,
  isPersonalizedExerciseType,
  isSubtypeFreeExerciseType,
} from "../../utils/teExecutionType";
import { ip_back } from "../../config";
import Loader from "../../components/Loading/Loader";
import { currentAppLanguage } from "../../utils/appLanguages";

export const Ein2 = () => {
  const [list, setlist] = useState();
  const [load, setload] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const { t } = useTranslation();
  const currentlng = currentAppLanguage();
  const hasWizardState = Boolean(data?.typeid);
  const isCbType = isCompetencyBlockExerciseType({
    type_name_en: data?.type_name_en,
    type: data?.type,
  });
  const typeIcon = exerciseTypeIconSrc({
    type_name_en: data?.type_name_en,
    type: data?.type,
  });

  useEffect(() => {
    if (!hasWizardState) {
      navigate("/SelectExerciseType", { replace: true, state: data || null });
      return;
    }
    typelist();
  }, [hasWizardState]);

  const typelist = async () => {
    if (!data?.typeid) return;
    try {
      if (isPersonalizedExerciseType({ type_name_en: data.type_name_en })) {
        navigate("/PersonalizedTrainingSlot", { replace: true, state: data });
        return;
      }
      // Warmup, Off-table & King of the Table: skip subtype picker — list all exercises for the type
      if (isSubtypeFreeExerciseType({ type_name_en: data.type_name_en })) {
        navigate("/Exerciseslist", {
          state: {
            subtypeid: null,
            subtype: null,
            typeid: data.typeid,
            type: data.type,
            type_name_en: data.type_name_en,
            sessionid: data.sessionid,
            grpid: data.grpid,
            tsg_id: data.tsg_id,
            te_id: data.te_id,
          },
          replace: true,
        });
        return;
      }

      // Pass typeid to filter subtypes by type
      const response = await getsubtype(data.typeid);
      console.log("🔍 Exercisesetup2 - Subtypes received:", response.data.data);
      console.log("🔍 Exercisesetup2 - Sample subtype:", response.data.data?.[0]);
      const subtypes = response.data.data || [];

      // If no subtypes exist for this type, skip directly to exercise list
      if (subtypes.length === 0) {
        navigate("/Exerciseslist", {
          state: {
            subtypeid: null,
            subtype: null,
            typeid: data.typeid,
            type: data.type,
            sessionid: data.sessionid,
            grpid: data.grpid,
            tsg_id: data.tsg_id,
            te_id: data.te_id,
          },
          replace: true,
        });
        return;
      }

      setlist(subtypes);
      setload(false);
    } catch (error) {
      console.error("Error fetching subtypes:", error);
      setload(false);
    }
  };

  /**
   * Get the appropriate icon for each exercise subtype
   */
  const getExerciseSubTypeIcon = (subTypeNameEn) => {
    console.log("🎨 Exercisesetup2 - Getting icon for:", subTypeNameEn);
    const iconMap = {
      // Match subtypes
      'Open Training': faDoorOpen,
      'Training Focus': faCrosshairs,
      'Competitive': faTrophy,
      
      // Technic Regularity subtypes
      'Forehand': faHandPaper,
      'Backhand': faHandBackFist,
      'Topspin': faCircleUp,
      'Chop': faCircleDown,
      'Pivot': faRotate,
      'Side-to-Side': faArrowsLeftRight,
      
      // Game Situation subtypes
      '3rd Ball Attack': faBomb,
      'Service Return': faReply,
      'Counter-Initiative': faShield,
      'Defensive Play': faShield,
      'Open Rally': faTableTennis,
      'Terminal Shot': faBullseye,
      
      // Service subtypes
      'Short': faRuler,
      'Backspin': faCircleDown,
      'Topspin': faCircleUp,
      'Sidespin': faCircle,
      'Long': faArrowRight,
      'Flat': faArrowUp,
      'Kicker': faGaugeHigh,
      
      // Multiball subtypes
      'Speed': faGaugeHigh,
      'Precision': faBullseye,
      'Footwork': faPersonWalking,
      'Endurance': faHeartbeat,
      
      // Shadow Training subtypes
      'Lateral Footwork': faArrowsLeftRight,
      'In–Out Footwork': faArrowsUpDown,
      
      // Warmup subtypes
      'Forehand Topspin': faCircleUp,
      'Backhand Topspin': faCircleUp,
      'Topspin Rally': faFire,
      
      // Drill subtypes
      'Obstacle Circuit': faRoad,
      'Ladder Footwork': faStairs,
      'Faster Time': faClock,
      
      // Personalized subtypes
      'Any Exercise': faStar,
      // Streaching subtypes
      'Arms / Shoulders': faDumbbell,
      'Back': faBackward,
      'Legs': faPersonRunning,
      'Global Post-Session': faGlobe
    };
    
    // Return the icon for the subtype, or default to faPlus if not found
    return iconMap[subTypeNameEn] || faPlus;
  };

  if (!hasWizardState) {
    return (
      <ExerciseAddLayout>
        <AddHeader title={t("addex")} />
        <Loader />
      </ExerciseAddLayout>
    );
  }

  return (
    <ExerciseAddLayout>
      <AddHeader title={t("addex")} />
      <p className="exercise_type_define exadd-crumb">
        {data?.type}
      </p>
      <div className="exercise_session_div exadd-choices">
        {list?.map((i, index) => {
          const blockIcon = isCbType ? competencyBlockIconSrc(i) : null;
          const mark = blockIcon || (!isCbType ? typeIcon : null);
          const markClass = blockIcon
            ? " exadd-choice__icon--mark"
            : mark
              ? " exadd-choice__icon--type"
              : "";
          return (
          <button
            type="button"
            className="session_add_box exadd-choice"
            key={i.sub_type_id || index}
            onClick={() => {
              if (i.sub_type_id === "sty20240607144727990") {
                navigate("/CompetencyBloc", {
                  state: {
                    id: i.sub_type_id,
                    subtype: i[`sub_type_name_${currentlng}`],
                    typeid: data.typeid,
                    type: data.type,
                    sessionid: data.sessionid,
                    grpid: data.grpid,
                    tsg_id: data.tsg_id,
                    te_id: data.te_id,
                  },
                });
              } else {
                const state = {
                  subtypeid: i.sub_type_id,
                  subtype: i[`sub_type_name_${currentlng}`],
                  typeid: data.typeid,
                  type: data.type,
                  type_name_en: data.type_name_en,
                  sessionid: data.sessionid,
                  grpid: data.grpid,
                  tsg_id: data.tsg_id,
                  te_id: data.te_id,
                };
                if (isCbType || data.type_name_en === "Blocks of competency") {
                  state.cbid = i.sub_type_id;
                  state.cb = i[`sub_type_name_${currentlng}`];
                }
                navigate("/Exerciseslist", { state });
              }
            }}
          >
            <div
              className={`session-image-add2 exadd-choice__icon${markClass}`}
              aria-hidden="true"
            >
              {mark ? (
                <ExerciseTypeMark src={mark} />
              ) : (
                <FontAwesomeIcon
                  icon={getExerciseSubTypeIcon(
                    i.sub_type_name_en || i[`sub_type_name_${currentlng}`] || "Unknown"
                  )}
                />
              )}
            </div>
            <div className="session-addinfo exadd-choice__copy">
              <h3>{i[`sub_type_name_${currentlng}`]} </h3>
            </div>
          </button>
          );
        })}
      </div>
      {load && <Loader />}
    </ExerciseAddLayout>
  );
};
